import { ComponentProps, PropsWithChildren, Ref, forwardRef } from "react";

import { VariantProps, cva } from "class-variance-authority";

import { cn } from "@/common/utils/css.utils";

type SelectBaseProps = {
  ref?: Ref<HTMLSelectElement>;
  elementRef?: Ref<HTMLSelectElement>;
  placeholder?: string;
  valid?: boolean;
} & VariantProps<typeof SelectStyle> &
  Omit<ComponentProps<"select">, "ref" | "color">;

const SelectStyle = cva(
  "appearance-none overflow-x-hidden text-ellipsis whitespace-nowrap rounded-[0.25rem] border bg-white bg-select-arrow bg-[length:1.5em_1.5em] bg-[position:right_0.5rem_center] bg-no-repeat py-2.5 pl-4 pr-10 font-body text-base text-black focus-within:ring-2 focus-within:ring-curious-200 focus:outline-none disabled:bg-grey-50 disabled:text-grey-600",
  {
    variants: {
      color: {
        brand: "",
        curious: "",
      },
      valid: {
        true: "",
        false: "",
      },
      disabled: {
        true: "cursor-not-allowed border-grey-400 bg-grey-50",
        false: "",
      },
    },
    compoundVariants: [
      /* Valid, not disabled */
      {
        valid: true,
        disabled: false,
        className: "border-grey-500",
      },
      /* Valid, not disabled, brand */
      {
        valid: true,
        disabled: false,
        color: "brand",
        className:
          "focus-within:border-brand-500 hover:border-brand-500 focus:border-brand-500",
      },
      /* Valid, not disabled, curious */
      {
        valid: true,
        disabled: false,
        color: "curious",
        className:
          "focus-within:border-curious-500 hover:border-curious-500 focus:border-curious-500",
      },
      /* Not valid, not disabled */
      {
        valid: false,
        disabled: false,
        className: "border-attention-500 focus-within:border-attention-500",
      },
    ],
    defaultVariants: {
      color: "curious",
      valid: true,
      disabled: false,
    },
  }
);

const SelectBase = ({
  className,
  elementRef,
  children,
  disabled,
  placeholder,
  valid,
  color,
  ...props
}: PropsWithChildren<SelectBaseProps>) => {
  return (
    <select
      ref={elementRef}
      {...props}
      className={cn(SelectStyle({ color, valid, disabled }), className)}
      disabled={disabled}
    >
      {placeholder && (
        <option value="disabled" disabled>
          {placeholder}
        </option>
      )}
      {children}
    </select>
  );
};

SelectBase.displayName = "Select";

export type SelectProps = Omit<SelectBaseProps, "elementRef">;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (props, ref) => <SelectBase {...props} elementRef={ref} />
);
